"use client";

import clsx from "clsx";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

import store, { type SettingsInitialTab } from "@/store/store";

type SettingsTab =
    | "tutorial"
    | "submit_question"
    | "notifications"
    | "team"
    | "feedback"
    | "prizes";

type MarkdownTextProps = {
    content?: string | null;
    className?: string;
    onSettingsNavigate?: (tab: SettingsTab) => void;
    /** Renders inline (span, paragraphs without <p>), e.g. inside a heading or a pill. Line breaks stay. */
    inline?: boolean;
};

const SETTINGS_TAB_TO_INITIAL_TAB: Record<
    SettingsTab,
    SettingsInitialTab
> = {
    tutorial: "tutorial_tab",
    submit_question: "submit_question_tab",
    notifications: "notifications_tab",
    team: "team_tab",
    feedback: "feedback_tab",
    prizes: "prizes_tab",
};

function getSettingsTab(href?: string): SettingsTab | null {
    if (!href?.startsWith("settings-tab:")) {
        return null;
    }

    const tab = href.slice("settings-tab:".length) as SettingsTab;

    return tab in SETTINGS_TAB_TO_INITIAL_TAB
        ? tab
        : null;
}

export default function MarkdownText({
    content,
    className,
    onSettingsNavigate,
    inline = false,
}: MarkdownTextProps) {
    if (!content) return null;

    const Wrapper = inline ? "span" : "div";

    return (
        <Wrapper
            className={clsx(
                "[&_p:last-child]:mb-0 [&_p]:mb-4 [&_ol]:pl-5 [&_ul]:pl-5 [&_strong]:font-bold [&_a]:underline [&_button]:underline [&_em]:italic [&_ol]:list-decimal [&_ul]:list-disc",
                className
            )}
        >
            <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkBreaks]}
                urlTransform={(url) => {
                    if (url.startsWith("settings-tab:")) {
                        return url;
                    }

                    return defaultUrlTransform(url);
                }}
                components={{
                    ...(inline ? { p: ({ children }) => <>{children}</> } : {}),
                    a: ({ href, children, node: _node, ...props }) => {
                        const settingsTab = getSettingsTab(href);

                        if (settingsTab) {
                            return (
                                <button
                                    type="button"
                                    className="inline bg-transparent p-0 border-0 font-inherit text-inherit cursor-pointer"
                                    onClick={() => {
                                        if (onSettingsNavigate) {
                                            onSettingsNavigate(settingsTab);
                                            return;
                                        }

                                        store.openSettings(
                                            SETTINGS_TAB_TO_INITIAL_TAB[settingsTab]
                                        );
                                    }}
                                >
                                    {children}
                                </button>
                            );
                        }

                        return (
                            <a
                                {...props}
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {children}
                            </a>
                        );
                    },
                }}
            >
                {content}
            </ReactMarkdown>
        </Wrapper>
    );
}